/**
 * DSTextField Component
 *
 * Text input wrapper that combines MUI TextField functionality
 * with design system styling. Maintains validation and focus states.
 *
 * Usage:
 * <DSTextField
 *   label="Patient Name"
 *   value={name}
 *   onChange={(e) => setName(e.target.value)}
 *   error={errors.name}
 *   hint="Required field"
 *   required
 * />
 */

import React from 'react';
import { TextField } from '@mui/material';
import FormField from './FormField';

export function DSTextField({
  label,
  value,
  onChange,
  error,
  hint,
  required,
  type = 'text',
  placeholder,
  disabled,
  multiline,
  rows,
  className = '',
  style = {},
  id,
  name,
  ...otherProps
}) {
  // Generate id from name or label if not provided
  const fieldId = id || name || (label ? label.toLowerCase().replace(/[^a-z0-9]+/g, '-') : undefined);

  return (
    <FormField
      label={label}
      error={error}
      hint={hint}
      required={required}
      className={className}
      style={style}
      id={fieldId}
    >
      <TextField
        id={fieldId}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={(type === 'date' || type === 'time') ? undefined : placeholder}
        disabled={disabled}
        multiline={multiline}
        rows={rows}
        error={!!error}
        variant="outlined"
        size="small"
        fullWidth
        InputLabelProps={{
          shrink: (type === 'date' || type === 'time') ? true : undefined,
          ...otherProps.InputLabelProps
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 'var(--radius-md)',
            '& fieldset': {
              borderColor: 'var(--neutral-300)',
              borderWidth: '1.5px',
            },
            '&:hover fieldset': {
              borderColor: 'var(--neutral-400)',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'var(--teal-600)',
              borderWidth: '1.5px',
            },
          },
          '& .MuiOutlinedInput-input': {
            fontFamily: 'var(--font-body)',
            fontSize: '1rem',
            padding: '10px 14px',
            color: 'var(--neutral-900)',
            '&::placeholder': {
              color: 'var(--neutral-400)',
              opacity: 1,
            },
          },
          '& .MuiOutlinedInput-input.Mui-disabled': {
            backgroundColor: 'var(--neutral-50)',
            color: 'var(--neutral-400)',
          },
          '& .MuiInputBase-root.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--error)',
          },
        }}
        {...otherProps}
      />
    </FormField>
  );
}
